import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { ChevronDown, ChevronRight, Settings, Globe } from 'lucide-react';
import { useComponentMetrics, getMetricObjectiveClassName, libelleTauxConversion, libelleConversions } from '../utils/metricsUtils';
import MetricsConfigModal from './MetricsConfigModal';
import ModuleVisibilityToggle from './ModuleVisibilityToggle';

import { calculateVariation, makeRenderVariation } from '../utils/variationUtils';
import { bornerLegende } from '../utils/legendeUtils';
/**
 * GA4GeographyChart - Composant pour afficher la géographie des visiteurs GA4
 * Structure: Pays > Région > Ville
 * Design identique au MatomoGeographyChart
 */
const GA4GeographyChart = ({
  geography = [],
  previousGeography = [],
  compareEnabled = false,
  formatNumber,
  loading = false,
  summaryBounceRate = null,
  conversionLabel = 'Conversions',
  serverTotals = null,
  serverPreviousTotals = null,
  // Props pour la configuration des métriques
  accountId = 'global',
  userRole = 'USER',
  isClientModuleVisible,
  toggleClientModuleVisibility,
  moduleId = 'ga4_geography',
  // Props pour les options avancées (conversions)
  token = null,
  propertyId = null,
  conversionConfig = null,
  onConversionConfigSave = null
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [expandedCountries, setExpandedCountries] = useState({});
  const [expandedRegions, setExpandedRegions] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'sessions', direction: 'desc' });
  const [showMetricsConfig, setShowMetricsConfig] = useState(false);
  const ITEMS_PER_PAGE = 10;

  // Hook pour les métriques du composant Géographie (réactif aux changements)
  const componentMetrics = useComponentMetrics('ga4_geography', accountId);

  // Fonction helper pour vérifier si une métrique est sélectionnée
  const isMetricActive = useCallback((metricKey) => {
    return componentMetrics.metrics.includes(metricKey);
  }, [componentMetrics.metrics]);

  // Ordre des métriques à afficher (depuis la config)
  const metricsOrder = componentMetrics.order || componentMetrics.metrics || [];

  const COLORS = ['#0088FE', '#82ca9d', '#FFBB28', '#FF8042', '#8884d8', '#00C49F', '#FF6B6B', '#4ECDC4'];


  // Fonction pour afficher une variation
  const renderVariation = makeRenderVariation({ fontSize: '11px', format: (v) => formatNumber(v, 1) });

  // Toggle functions
  const toggleCountry = (label) => {
    setExpandedCountries(prev => ({ ...prev, [label]: !prev[label] }));
  };

  const toggleRegion = (countryLabel, regionLabel) => {
    const key = `${countryLabel}-${regionLabel}`;
    setExpandedRegions(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Handler de tri
  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Données triées
  const sortedGeography = useMemo(() => {
    if (!geography || !geography.length) return [];

    const sorted = [...geography];
    sorted.sort((a, b) => {
      let aValue, bValue;

      if (sortConfig.key === 'country') {
        aValue = a.country || '';
        bValue = b.country || '';
        return sortConfig.direction === 'asc'
          ? String(aValue).localeCompare(String(bValue))
          : String(bValue).localeCompare(String(aValue));
      }

      if (sortConfig.key === 'conversionRate') {
        aValue = a.sessions > 0 ? (a.conversions / a.sessions * 100) : 0;
        bValue = b.sessions > 0 ? (b.conversions / b.sessions * 100) : 0;
      } else {
        aValue = Number(a[sortConfig.key]) || 0;
        bValue = Number(b[sortConfig.key]) || 0;
      }

      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    });
    return sorted;
  }, [geography, sortConfig]);

  // Calculate totals
  const totals = useMemo(() => {
    // Le total d'une période ne dépend pas de la dimension : on affiche celui que GA4 a
    // calculé pour l'ensemble du site, jamais une addition des lignes reçues.
    if (serverTotals && serverTotals.sessions > 0) return serverTotals;

    if (!geography || !geography.length) return { sessions: 0, activeUsers: 0, pageviews: 0, bounceRate: 0, avgSessionDuration: 0, conversions: 0, conversionsValue: 0, transactions: 0, totalRevenue: 0 };

    const result = geography.reduce((acc, country) => ({
      sessions: acc.sessions + (country.sessions || 0),
      activeUsers: acc.activeUsers + (country.activeUsers || 0),
      pageviews: acc.pageviews + (country.pageviews || 0),
      bounceSum: acc.bounceSum + ((country.bounceRate || 0) * (country.sessions || 0)),
      durationSum: acc.durationSum + ((country.avgSessionDuration || 0) * (country.sessions || 0)),
      conversions: acc.conversions + (country.conversions || 0),
      conversionsValue: acc.conversionsValue + (country.conversionsValue || 0),
      transactions: acc.transactions + (country.transactions || 0),
      totalRevenue: acc.totalRevenue + (country.totalRevenue || 0)
    }), { sessions: 0, activeUsers: 0, pageviews: 0, bounceSum: 0, durationSum: 0, conversions: 0, conversionsValue: 0, transactions: 0, totalRevenue: 0 });

    // Calcul du taux de rebond et durée moyenne pondérés par sessions
    result.bounceRate = result.sessions > 0 ? result.bounceSum / result.sessions : 0;
    result.avgSessionDuration = result.sessions > 0 ? result.durationSum / result.sessions : 0;
    result.transactionRate = result.sessions > 0 ? (result.transactions / result.sessions * 100) : 0;

    return result;
  }, [geography, serverTotals]);

  // Calcul des totaux de la période précédente pour comparaison
  const previousTotals = useMemo(() => {
    if (!compareEnabled || !previousGeography || !previousGeography.length) return null;

    // Même règle que pour le total courant : la période de comparaison a elle aussi
    // son total calculé par GA4, sinon la variation compare deux choses différentes.
    if (serverPreviousTotals && serverPreviousTotals.sessions > 0) return serverPreviousTotals;

    const result = previousGeography.reduce((acc, country) => ({
      sessions: acc.sessions + (country.sessions || 0),
      activeUsers: acc.activeUsers + (country.activeUsers || 0),
      pageviews: acc.pageviews + (country.pageviews || 0),
      bounceSum: acc.bounceSum + ((country.bounceRate || 0) * (country.sessions || 0)),
      durationSum: acc.durationSum + ((country.avgSessionDuration || 0) * (country.sessions || 0)),
      conversions: acc.conversions + (country.conversions || 0),
      conversionsValue: acc.conversionsValue + (country.conversionsValue || 0),
      transactions: acc.transactions + (country.transactions || 0),
      totalRevenue: acc.totalRevenue + (country.totalRevenue || 0)
    }), { sessions: 0, activeUsers: 0, pageviews: 0, bounceSum: 0, durationSum: 0, conversions: 0, conversionsValue: 0, transactions: 0, totalRevenue: 0 });

    result.bounceRate = result.sessions > 0 ? result.bounceSum / result.sessions : 0;
    result.avgSessionDuration = result.sessions > 0 ? result.durationSum / result.sessions : 0;
    result.transactionRate = result.sessions > 0 ? (result.transactions / result.sessions * 100) : 0;

    return result;
  }, [compareEnabled, previousGeography, serverPreviousTotals]);

  // Données pour le pie chart Sessions
  const sessionsChartData = useMemo(() => {
    if (!geography || !geography.length) return [];

    return geography
      .slice(0, 8) // Top 8 pays pour le graphique
      .map(country => ({
        name: country.country,
        value: country.sessions || 0,
        percentage: totals.sessions > 0 ? ((country.sessions / totals.sessions) * 100).toFixed(1) : 0
      }))
      .filter(item => parseFloat(item.percentage) > 0);
  }, [geography, totals.sessions]);

  // Données pour le pie chart Conversions
  const conversionsChartData = useMemo(() => {
    if (!geography || !geography.length) return [];

    return geography
      .slice(0, 8)
      .map(country => ({
        name: country.country,
        value: country.conversions || 0,
        percentage: totals.conversions > 0 ? ((country.conversions / totals.conversions) * 100).toFixed(1) : 0
      }))
      .filter(item => parseFloat(item.percentage) > 0);
  }, [geography, totals.conversions]);

  // Données pour le pie chart Transactions (e-commerce)
  const transactionsChartData = useMemo(() => {
    if (!geography || !geography.length) return [];

    return geography
      .slice(0, 8)
      .map(country => ({
        name: country.country,
        value: country.transactions || 0,
        percentage: totals.transactions > 0 ? ((country.transactions / totals.transactions) * 100).toFixed(1) : 0
      }))
      .filter(item => parseFloat(item.percentage) > 0);
  }, [geography, totals.transactions]);

  // Détermine si on affiche Transactions ou Conversions dans le pie chart
  const showTransactionsPieChart = isMetricActive('transactions') && !isMetricActive('conv');
  const pieChartData = showTransactionsPieChart ? transactionsChartData : conversionsChartData;
  const pieChartLabel = showTransactionsPieChart
    ? 'Transactions'
    : libelleConversions(conversionConfig, conversionLabel);
  const pieChartTotal = showTransactionsPieChart ? totals.transactions : totals.conversions;

  // Couleur figée par pays, sur l'ordre brut : sans ça, trier ou paginer le tableau
  // désynchronise ses pastilles des parts du camembert
  const countryColorMap = useMemo(() => {
    const map = {};
    (geography || []).forEach((country, index) => {
      map[country.country] = COLORS[index % COLORS.length];
    });
    return map;
  }, [geography]);

  // Données pour la légende
  const legendData = useMemo(() => {
    if (!geography || !geography.length) return [];

    return geography.slice(0, 8).map((country) => ({
      value: country.country,
      color: countryColorMap[country.country]
    }));
  }, [geography, countryColorMap]);

  // Dix étiquettes au plus : au-delà, la colonne s'étire sur plusieurs écrans et désigne des
  // parts invisibles. Le camembert et les totaux, eux, portent toujours sur tout.
  const { visibles: legendeVisible, reste: legendeReste } = bornerLegende(legendData);

  // Configuration des colonnes du tableau
  const columnConfig = useMemo(() => ({
    visites: {
      key: 'sessions',
      label: 'Sessions',
      getValue: (item) => item.sessions || 0,
      format: (val) => formatNumber(val),
      getPrevValue: (prev) => prev?.sessions || 0
    },
    visiteurs: {
      key: 'activeUsers',
      label: 'Utilisateurs',
      getValue: (item) => item.activeUsers || 0,
      format: (val) => formatNumber(val),
      getPrevValue: (prev) => prev?.activeUsers || 0
    },
    rebond: {
      key: 'bounceRate',
      label: '% de Rebond',
      getValue: (item) => item.bounceRate || 0,
      format: (val) => `${formatNumber(val, 1)}%`,
      getPrevValue: (prev) => prev?.bounceRate || 0,
      inverted: true
    },
    pages_vues: {
      key: 'pageviews',
      label: 'Pages vues',
      getValue: (item) => item.pageviews || 0,
      format: (val) => formatNumber(val),
      getPrevValue: (prev) => prev?.pageviews || 0
    },
    taux_conv: {
      key: 'conversionRate',
      label: libelleTauxConversion(conversionConfig, conversionLabel),
      getValue: (item) => item.sessions > 0 ? (item.conversions / item.sessions * 100) : 0,
      format: (val) => `${formatNumber(val, 2)}%`,
      getPrevValue: (prev) => prev && prev.sessions > 0 ? (prev.conversions / prev.sessions * 100) : 0
    },
    conv: {
      key: 'conversions',
      label: libelleConversions(conversionConfig, conversionLabel),
      getValue: (item) => item.conversions || 0,
      format: (val) => formatNumber(val),
      getPrevValue: (prev) => prev?.conversions || 0
    },
    valeur_conv: {
      key: 'conversionsValue',
      label: 'Valeur Conv.',
      getValue: (item) => item.conversionsValue || 0,
      format: (val) => `${formatNumber(val, 2)}€`,
      getPrevValue: (prev) => prev?.conversionsValue || 0
    },
    transactions: {
      key: 'transactions',
      label: 'Transactions',
      getValue: (item) => item.transactions || 0,
      format: (val) => formatNumber(val),
      getPrevValue: (prev) => prev?.transactions || 0
    },
    chiffre_affaires: {
      key: 'totalRevenue',
      label: 'Chiffre d\'affaires',
      getValue: (item) => item.totalRevenue || 0,
      format: (val) => `${formatNumber(val, 2)}€`,
      getPrevValue: (prev) => prev?.totalRevenue || 0
    },
    taux_transaction: {
      key: 'transactionRate',
      label: '% Conversion (transactions)',
      getValue: (item) => item.sessions > 0 ? (item.transactions / item.sessions * 100) : 0,
      format: (val) => `${formatNumber(val, 2)}%`,
      getPrevValue: (prev) => prev && prev.sessions > 0 ? (prev.transactions / prev.sessions * 100) : 0
    },
    panier_moyen: {
      key: 'avgOrderValue',
      label: 'Panier moyen',
      getValue: (item) => item.transactions > 0 ? (item.totalRevenue || 0) / item.transactions : 0,
      format: (val) => `${formatNumber(val, 2)}€`,
      getPrevValue: (prev) => prev && prev.transactions > 0 ? (prev.totalRevenue || 0) / prev.transactions : 0
    },
    duree_moy: {
      key: 'avgSessionDuration',
      label: 'Durée moyenne',
      getValue: (item) => item.avgSessionDuration || 0,
      format: (val) => {
        const mins = Math.floor(val / 60);
        const secs = Math.floor(val % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
      },
      getPrevValue: (prev) => prev?.avgSessionDuration || 0
    },
    pages_visite: {
      key: 'pagesPerSession',
      label: 'Pages/Session',
      getValue: (item) => item.sessions > 0 ? (item.pageviews || item.pageViews || 0) / item.sessions : 0,
      format: (val) => formatNumber(val, 1),
      getPrevValue: (prev) => prev && prev.sessions > 0 ? (prev.pageviews || prev.pageViews || 0) / prev.sessions : 0
    }
  }), [formatNumber, conversionLabel, conversionConfig]);

  // Fonction helper pour rendre les cellules de métriques d'une ligne
  const renderMetricCells = useCallback((item, prev, style = {}) => {
    return metricsOrder.map(metricKey => {
      if (!isMetricActive(metricKey) || !columnConfig[metricKey]) return null;
      const col = columnConfig[metricKey];
      const value = col.getValue(item);
      const prevValue = prev ? col.getPrevValue(prev) : null;
      const objectiveClass = getMetricObjectiveClassName(metricKey, value, 'ga4_geography', accountId);
      return (
        <td key={metricKey} className={`numeric ${objectiveClass}`} style={objectiveClass ? {} : style}>
          {col.format(value)}
          {prev && renderVariation(calculateVariation(value, prevValue), col.inverted)}
        </td>
      );
    });
  }, [metricsOrder, isMetricActive, columnConfig, renderVariation, calculateVariation, accountId]);

  // Pagination
  const totalPages = Math.ceil((sortedGeography?.length || 0) / ITEMS_PER_PAGE);

  // Revenir à la première page si les données raccourcissent la liste
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  const paginatedGeography = useMemo(() => {
    if (!sortedGeography || !sortedGeography.length) return [];
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return sortedGeography.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [sortedGeography, currentPage]);

  if (loading) {
    return (
      <>
        <div className="section-header">
          <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Globe size={18} />
            Géographie
          </h4>
        </div>
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
          Chargement des données...
        </div>
      </>
    );
  }

  if (!geography || !geography.length) {
    return null;
  }

  return (
    <>
      <div className="section-header">
        <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Globe size={18} />
          Géographie
        </h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {userRole !== 'CLIENT' && (
            <button
              onClick={() => setShowMetricsConfig(true)}
              className="metrics-settings-btn"
              title="Configurer les métriques"
            >
              <Settings size={16} />
            </button>
          )}
          {toggleClientModuleVisibility ? (
            <ModuleVisibilityToggle
              isVisible={isClientModuleVisible ? isClientModuleVisible(moduleId) : true}
              onToggle={toggleClientModuleVisibility}
              userRole={userRole}
              moduleId={moduleId}
              title="Géographie GA4"
            />
          ) : (
            <button
              onClick={() => setIsVisible(!isVisible)}
              className="section-toggle-btn"
              title={isVisible ? "Masquer la section" : "Afficher la section"}
            >
              {isVisible ? '−' : '+'}
            </button>
          )}
        </div>
      </div>

      {isVisible && (
        <>
          {/* Graphiques en secteurs côte à côte */}
          <div style={{ background: 'white', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                  {/* Pie chart Sessions (gauche) */}
                  <div style={{ flex: 1, textAlign: 'center' }}>
                    <h5 style={{ margin: '0 0 10px 0', color: '#374151', fontSize: '14px' }}>Sessions par pays</h5>
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie
                          data={sessionsChartData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ percentage }) => `${percentage}%`}
                          outerRadius={70}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {sessionsChartData.map((entry, index) => (
                            <Cell key={`sessions-cell-${index}`} fill={countryColorMap[entry.name] || COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value, name) => [formatNumber(value) + ' sessions', name]} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Légende commune (centre) */}
                  <div style={{
                    minWidth: '150px',
                    padding: '0 20px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}>
                    {legendeVisible.map((item, index) => (
                      <div key={index} style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: '8px',
                        fontSize: '13px'
                      }}>
                        <div style={{
                          width: '12px',
                          height: '12px',
                          backgroundColor: item.color,
                          marginRight: '8px',
                          borderRadius: '2px'
                        }}></div>
                        <span>{item.value}</span>
                      </div>
                    ))}
                    {/* Une liste qui s'arrête en silence se lit comme une liste complète. */}
                    {legendeReste > 0 && (
                      <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                        + {legendeReste} autres
                      </div>
                    )}
                  </div>

                  {/* Pie chart Conversions/Transactions (droite) */}
                  <div style={{ flex: 1, textAlign: 'center' }}>
                    <h5 style={{ margin: '0 0 10px 0', color: '#374151', fontSize: '14px' }}>{pieChartLabel} par pays</h5>
                    {pieChartTotal > 0 ? (
                      <ResponsiveContainer width="100%" height={280}>
                        <PieChart>
                          <Pie
                            data={pieChartData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ percentage }) => `${percentage}%`}
                            outerRadius={70}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {pieChartData.map((entry, index) => (
                              <Cell key={`pie-cell-${index}`} fill={countryColorMap[entry.name] || COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value, name) => [formatNumber(value) + ` ${pieChartLabel.toLowerCase()}`, name]} />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div style={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
                        Aucune {pieChartLabel.toLowerCase()}
                      </div>
                    )}
                  </div>
                </div>
              </div>

          {/* Tableau détaillé */}
          <div className="device-table-wrapper" style={{ marginTop: '0', paddingTop: '0' }}>
            <div className="device-summary-table" style={{ marginTop: '0', paddingTop: '0' }}>
              <table
                className="devices-table"
                style={{ width: '100%', tableLayout: 'auto', borderCollapse: 'collapse' }}
              >
                <thead>
                  <tr>
                    <th onClick={() => handleSort('country')} className="sortable" style={{ cursor: 'pointer' }}>
                      Localisation {sortConfig.key === 'country' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                    {metricsOrder.map(metricKey => {
                      if (!isMetricActive(metricKey) || !columnConfig[metricKey]) return null;
                      const col = columnConfig[metricKey];
                      return (
                        <th key={metricKey} onClick={() => handleSort(col.key)} className="sortable numeric" style={{ cursor: 'pointer' }}>
                          <span className="th-libelle">{col.label}</span> {sortConfig.key === col.key && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      );
                    })}
                  </tr>
                </thead>

                <tbody>
                  {paginatedGeography.map((country, index) => {
                    const globalIndex = (currentPage - 1) * ITEMS_PER_PAGE + index;
                    const isCountryExpanded = expandedCountries[country.country];
                    const hasRegions = country.regions && country.regions.length > 0;
                    const prevCountry = compareEnabled && previousGeography ? previousGeography.find(p => p.country === country.country) : null;

                    return (
                      <React.Fragment key={`country-${index}`}>
                        {/* Country row */}
                        <tr
                          className={hasRegions ? 'expandable-row' : ''}
                          onClick={() => hasRegions && toggleCountry(country.country)}
                          style={{ cursor: hasRegions ? 'pointer' : 'default', fontWeight: '500' }}
                        >
                          <td className="device-name">
                            {hasRegions && (
                              <span style={{ marginRight: '4px', display: 'inline-flex', alignItems: 'center' }}>
                                {isCountryExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                              </span>
                            )}
                            <span
                              className="device-indicator"
                              style={{ backgroundColor: countryColorMap[country.country] || COLORS[globalIndex % COLORS.length] }}
                            ></span>
                            {country.country}
                          </td>
                          {renderMetricCells(country, prevCountry)}
                        </tr>

                        {/* Regions */}
                        {isCountryExpanded && country.regions && country.regions.map((region, regionIndex) => {
                          const regionKey = `${country.country}-${region.region}`;
                          const isRegionExpanded = expandedRegions[regionKey];
                          const hasCities = region.cities && region.cities.length > 0;
                          const prevRegion = prevCountry?.regions?.find(r => r.region === region.region);

                          return (
                            <React.Fragment key={`region-${index}-${regionIndex}`}>
                              {/* Region row */}
                              <tr
                                className={hasCities ? 'expandable-row detail-row' : 'detail-row'}
                                onClick={(e) => {
                                  if (hasCities) {
                                    e.stopPropagation();
                                    toggleRegion(country.country, region.region);
                                  }
                                }}
                                style={{
                                  cursor: hasCities ? 'pointer' : 'default',
                                  backgroundColor: '#f9fafb',
                                  fontSize: '0.95em'
                                }}
                              >
                                <td className="device-name" style={{ paddingLeft: '28px' }}>
                                  {hasCities && (
                                    <span style={{ marginRight: '4px', display: 'inline-flex', alignItems: 'center' }}>
                                      {isRegionExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                                    </span>
                                  )}
                                  {!hasCities && <span style={{ color: '#9ca3af' }}>└</span>}
                                  {' '}{region.region}
                                </td>
                                {renderMetricCells(region, prevRegion, { color: '#4b5563' })}
                              </tr>

                              {/* Cities */}
                              {isRegionExpanded && region.cities && region.cities.map((city, cityIndex) => {
                                const prevCity = prevRegion?.cities?.find(c => c.city === city.city);

                                return (
                                  <tr
                                    key={`city-${index}-${regionIndex}-${cityIndex}`}
                                    style={{
                                      backgroundColor: '#f3f4f6',
                                      fontSize: '0.9em'
                                    }}
                                  >
                                    <td className="device-name" style={{ paddingLeft: '48px' }}>
                                      <span style={{ color: '#9ca3af' }}>└</span> {city.city}
                                    </td>
                                    {renderMetricCells(city, prevCity, { color: '#6b7280' })}
                                  </tr>
                                );
                              })}
                            </React.Fragment>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </tbody>

                <tfoot>
                  <tr className="total-row">
                    <td className="device-name"><strong>TOTAL</strong></td>
                    {metricsOrder.map(metricKey => {
                      if (!isMetricActive(metricKey) || !columnConfig[metricKey]) return null;
                      const col = columnConfig[metricKey];
                      // Cas spécial pour le rebond : utiliser summaryBounceRate si disponible
                      const value = metricKey === 'rebond' ? (summaryBounceRate ?? totals.bounceRate ?? 0) : col.getValue(totals);
                      const prevValue = previousTotals ? col.getPrevValue(previousTotals) : null;
                      const objectiveClass = getMetricObjectiveClassName(metricKey, value, 'ga4_geography', accountId);
                      return (
                        <td key={metricKey} className={`numeric ${objectiveClass}`}>
                          <strong>{col.format(value)}</strong>
                          {previousTotals && renderVariation(calculateVariation(value, prevValue), col.inverted)}
                        </td>
                      );
                    })}
                  </tr>
                </tfoot>
              </table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination-controls">
                  <div className="pagination-info">
                    Affichage {((currentPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, sortedGeography.length)} sur {sortedGeography.length} pays
                  </div>
                  <div className="pagination-buttons">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="pagination-btn"
                    >
                      Précédent
                    </button>
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="pagination-btn"
                    >
                      Suivant
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Modal de configuration des métriques */}
      <MetricsConfigModal
        isOpen={showMetricsConfig}
        onClose={() => setShowMetricsConfig(false)}
        componentId="ga4_geography"
        accountId={accountId}
        token={token}
        propertyId={propertyId}
        currentAdvancedConfig={conversionConfig}
        onAdvancedConfigSave={onConversionConfigSave}
      />
    </>
  );
};

export default GA4GeographyChart;
